import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { PageMessage } from '../components/PageMessage'
import { LIFECYCLE_PHASES, OWNER_ROLES, type LifecyclePhase } from '../features/templates/phases'
import {
  moveInOrder,
  useAddItem,
  useAddSection,
  useChecklistTemplate,
  useDeleteChecklistTemplate,
  useDeleteItem,
  useDeleteSection,
  usePublishChecklistTemplate,
  useRetireChecklistTemplate,
  useReorderItems,
  useReorderSections,
  useUpdateChecklistTemplate,
  useUpdateItem,
  type ChecklistSection,
} from '../features/templates/checklistApi'

export function ChecklistTemplateEditorPage() {
  const { templateId = '' } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const { data: template, isPending, isError, refetch } = useChecklistTemplate(templateId)

  const update = useUpdateChecklistTemplate(templateId)
  const publish = usePublishChecklistTemplate(templateId)
  const addSection = useAddSection(templateId)
  const deleteSection = useDeleteSection(templateId)
  const remove = useDeleteChecklistTemplate(templateId)
  const retire = useRetireChecklistTemplate(templateId)
  const reorderSections = useReorderSections(templateId)
  const addItem = useAddItem(templateId)
  const updateItem = useUpdateItem(templateId)
  const deleteItem = useDeleteItem(templateId)
  const reorderItems = useReorderItems(templateId)

  const [sectionTitle, setSectionTitle] = useState('')
  const [sectionPhase, setSectionPhase] = useState<LifecyclePhase | ''>('')
  const [itemDrafts, setItemDrafts] = useState<Record<string, string>>({})
  // Per section, like the title above: two sections being filled in turn must
  // not share one owner choice.
  const [itemOwners, setItemOwners] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  if (isPending) return <p>Loading checklist…</p>
  if (isError || !template) {
    return (
      <PageMessage title="Checklist not found" body="That checklist template does not exist." />
    )
  }

  const isPublished = template.status === 'Published'
  const canEdit = can('Coordinator') && !isPublished
  const itemCount = template.sections.reduce((total, section) => total + section.items.length, 0)

  const report = (err: unknown) =>
    setError(err instanceof ApiError ? err.detail : 'That change could not be applied.')

  const run = async (action: () => Promise<unknown>) => {
    setError(null)
    try {
      await action()
      await refetch()
    } catch (err) {
      report(err)
    }
  }

  const moveSection = (section: ChecklistSection, direction: -1 | 1) =>
    run(() => reorderSections.mutateAsync(moveInOrder(template.sections, section.id, direction)))

  return (
    <section>
      <div className="page-header">
        <div>
          <h1>{template.name}</h1>
          <div className="card-meta">
            <span>Version {template.version}</span>
            <span className={`status status-${template.status.toLowerCase()}`}>
              {template.status}
            </span>
            <span>Default phase: {template.defaultPhaseDisplayName}</span>
            {template.isRetired ? <span className="badge">Retired</span> : null}
            {template.isUsedByAPlan ? <span className="badge">In use by a plan</span> : null}
          </div>
        </div>

        {/*
          Same header treatment as the timeline editor, and for the same
          reason. Retire and Delete are new here: the API has exposed both from
          the start and the spec has required delete, but this page never wired
          them up, so a mistyped checklist template was permanent from the UI.
        */}
        <div className="header-actions">
          {can('Coordinator') && !isPublished ? (
            <button
              type="button"
              className="primary"
              disabled={itemCount === 0 || publish.isPending}
              onClick={() => run(() => publish.mutateAsync())}
            >
              Publish
            </button>
          ) : null}

          {can('Coordinator') && isPublished ? (
            <button
              type="button"
              className="primary"
              disabled={update.isPending}
              onClick={async () => {
                setError(null)
                try {
                  const next = await update.mutateAsync({
                    name: template.name,
                    description: template.description,
                    defaultPhase: template.defaultPhase,
                  })
                  navigate(`/templates/checklist/${next.id}`)
                } catch (err) {
                  report(err)
                }
              }}
            >
              Create draft version {template.version + 1}
            </button>
          ) : null}

          {can('Admin') ? (
            <>
              <button
                type="button"
                disabled={retire.isPending || template.isRetired}
                title="Stops this template being offered for new plans. Plans already built from it are untouched."
                onClick={() => run(() => retire.mutateAsync())}
              >
                Retire
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                title="Refused once any plan has been built from this template."
                onClick={async () => {
                  setError(null)

                  if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) {
                    return
                  }

                  try {
                    await remove.mutateAsync()
                    navigate('/templates')
                  } catch (err) {
                    // 4.2: the server's refusal names what to do instead
                    // ("retire it"), so it is shown verbatim rather than
                    // replaced with a generic failure.
                    report(err)
                  }
                }}
              >
                Delete
              </button>
            </>
          ) : null}
        </div>
      </div>

      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}

      {isPublished ? (
        <p className="notice">
          This version is published and frozen. Editing it creates the next draft, so plans already
          built from it keep exactly what was published.
        </p>
      ) : null}

      {/*
        Section authoring left, the checklist right. The add-ITEM forms stay
        inside their sections (see below) - only section-level authoring
        belongs in this column.
      */}
      <div className={canEdit ? 'editor-columns' : undefined}>
        {canEdit ? (
          <div className="editor-authoring">
            <form
              className="form"
              onSubmit={(event) => {
                event.preventDefault()
                if (!sectionTitle.trim()) return
                void run(async () => {
                  await addSection.mutateAsync({
                    title: sectionTitle.trim(),
                    phase: sectionPhase === '' ? null : sectionPhase,
                  })
                  setSectionTitle('')
                  setSectionPhase('')
                })
              }}
            >
              <h2>Add section</h2>

              <div className="field">
                <label htmlFor="section-title">Title</label>
                <input
                  id="section-title"
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="section-phase">Phase</label>
                <select
                  id="section-phase"
                  value={sectionPhase}
                  onChange={(e) => setSectionPhase(e.target.value as LifecyclePhase | '')}
                >
                  <option value="">Inherit ({template.defaultPhaseDisplayName})</option>
                  {LIFECYCLE_PHASES.map((phase) => (
                    <option key={phase.value} value={phase.value}>
                      {phase.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="primary">
                  Add section
                </button>
              </div>
            </form>
          </div>
        ) : null}

        <div className="editor-content">
          {template.sections.length === 0 ? (
            <p className="empty">
              No sections yet.{canEdit ? ' Add the first one on the left.' : ''}
            </p>
          ) : null}

          {template.sections.map((section, index) => (
            <div key={section.id} className="panel">
              <div className="page-header">
                <div>
                  <h2>{section.title}</h2>
                  <div className="card-meta">
                    <span>
                      {section.effectivePhaseDisplayName}
                      {section.overridesTemplatePhase ? ' (overrides default)' : ' (inherited)'}
                    </span>
                  </div>
                </div>

                {canEdit ? (
                  <div className="form-actions">
                    <button
                      type="button"
                      aria-label={`Move ${section.title} up`}
                      disabled={index === 0}
                      onClick={() => moveSection(section, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${section.title} down`}
                      disabled={index === template.sections.length - 1}
                      onClick={() => moveSection(section, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => run(() => deleteSection.mutateAsync(section.id))}
                    >
                      Delete section
                    </button>
                  </div>
                ) : null}
              </div>

              {/*
            Above this section's items, not below them. Adding several items
            in a row meant scrolling past everything already added to reach
            the form again, and worse the longer the section got - the same
            complaint as the page-level one, one level down. It stays inside
            its section so the section an item joins needs no selector and
            cannot be got wrong.
          */}
              {canEdit ? (
                <form
                  className="inline-form"
                  onSubmit={(event) => {
                    event.preventDefault()
                    const title = (itemDrafts[section.id] ?? '').trim()
                    if (!title) return
                    void run(async () => {
                      await addItem.mutateAsync({
                        sectionId: section.id,
                        title,
                        description: null,
                        ownerRole: itemOwners[section.id] || null,
                        requiresNote: false,
                        requiresAttachment: false,
                      })
                      setItemDrafts({ ...itemDrafts, [section.id]: '' })

                      // The owner is deliberately NOT cleared. Items in a
                      // section usually share one, and re-picking it for every
                      // item is the typing this change exists to remove.
                    })
                  }}
                >
                  <div className="field">
                    <label htmlFor={`item-${section.id}`}>New item</label>
                    <input
                      id={`item-${section.id}`}
                      value={itemDrafts[section.id] ?? ''}
                      onChange={(e) =>
                        setItemDrafts({ ...itemDrafts, [section.id]: e.target.value })
                      }
                      placeholder="Unpack and power on badge printers"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor={`item-owner-${section.id}`}>Owner role</label>
                    <select
                      id={`item-owner-${section.id}`}
                      value={itemOwners[section.id] ?? ''}
                      onChange={(e) =>
                        setItemOwners({ ...itemOwners, [section.id]: e.target.value })
                      }
                    >
                      <option value="">Nobody in particular</option>
                      {OWNER_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="submit">Add item</button>
                </form>
              ) : null}

              {section.items.length === 0 ? (
                <p className="empty">No items in this section.</p>
              ) : (
                <ul className="card-list">
                  {section.items.map((item, itemIndex) => (
                    <li key={item.id} className="card">
                      <div className="card-title">{item.title}</div>
                      <div className="card-tags">
                        {item.requiresNote ? <span className="badge">Note required</span> : null}
                        {item.requiresAttachment ? (
                          <span className="badge">Attachment required</span>
                        ) : null}
                        {item.ownerRole ? <span className="badge">{item.ownerRole}</span> : null}
                      </div>

                      {canEdit ? (
                        <div className="card-tags">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={item.requiresNote}
                              onChange={(e) =>
                                run(() =>
                                  updateItem.mutateAsync({
                                    sectionId: section.id,
                                    id: item.id,
                                    title: item.title,
                                    description: item.description,
                                    ownerRole: item.ownerRole,
                                    requiresNote: e.target.checked,
                                    requiresAttachment: item.requiresAttachment,
                                  }),
                                )
                              }
                            />
                            Note
                          </label>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={item.requiresAttachment}
                              onChange={(e) =>
                                run(() =>
                                  updateItem.mutateAsync({
                                    sectionId: section.id,
                                    id: item.id,
                                    title: item.title,
                                    description: item.description,
                                    ownerRole: item.ownerRole,
                                    requiresNote: item.requiresNote,
                                    requiresAttachment: e.target.checked,
                                  }),
                                )
                              }
                            />
                            Attachment
                          </label>
                          <button
                            type="button"
                            aria-label={`Move ${item.title} up`}
                            disabled={itemIndex === 0}
                            onClick={() =>
                              run(() =>
                                reorderItems.mutateAsync({
                                  sectionId: section.id,
                                  ids: moveInOrder(section.items, item.id, -1),
                                }),
                              )
                            }
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${item.title} down`}
                            disabled={itemIndex === section.items.length - 1}
                            onClick={() =>
                              run(() =>
                                reorderItems.mutateAsync({
                                  sectionId: section.id,
                                  ids: moveInOrder(section.items, item.id, 1),
                                }),
                              )
                            }
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              run(() =>
                                deleteItem.mutateAsync({ sectionId: section.id, id: item.id }),
                              )
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
